import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "../components/ConfirmDialog";
import RoleModal from "../components/RoleModal";
import Toast from "../components/Toast";
import { apiErrorMessage } from "../services/api";
import { deleteRole, fetchPermissionCatalogue, fetchRoles } from "../services/roleService";

/**
 * The business's roles. It starts with four — owner, branch manager,
 * receptionist and staff — and may add as many of its own as it likes, each
 * carrying whichever permissions it ticks.
 */
export default function Roles() {
  const { t, i18n } = useTranslation();

  const [roles, setRoles] = useState([]);
  const [catalogue, setCatalogue] = useState({});
  // Which permissions each grant carries on its own — a write implies its
  // module's read. Comes from the API with the catalogue.
  const [implies, setImplies] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, catalog] = await Promise.all([fetchRoles(), fetchPermissionCatalogue()]);
      setRoles(list);
      setCatalogue(catalog.permissions);
      setImplies(catalog.implies);
    } catch (err) {
      setError(apiErrorMessage(err, t("roles.loadFailed")));
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteRole(deleting.id);
      setToast({ type: "success", message: t("roles.deleted", { name: deleting.name }) });
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("roles.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  /** System roles are labelled in the user's language; custom ones as typed. */
  const label = (role) => (role.slug ? t(`roles.system.${role.slug}`, role.name) : role.name);

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("roles.title")}</h1>
          <p className="text-sm text-ink-2">{t("roles.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="btn-primary"
        >
          {t("roles.new")}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : roles.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">{t("roles.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("roles.table.name")}</th>
                  <th className="px-4 py-3">{t("roles.table.permissions")}</th>
                  <th className="px-4 py-3">{t("roles.table.users")}</th>
                  <th className="px-4 py-3 text-end">{t("roles.table.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td className="px-4 py-3">
                      <span className="font-medium">{label(role)}</span>
                      {role.is_system && (
                        <span className="ms-2 rounded bg-raised px-2 py-1 text-xs text-ink-2">
                          {t("roles.systemBadge")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {/* Counted as the server sees it: the reads a write
                          implies are permissions this role really carries,
                          even though no row was stored for them. */}
                      {role.is_owner
                        ? t("roles.allPermissions")
                        : t("roles.permissionCount", {
                            count: (role.effective_permissions ?? role.permissions).length,
                          })}
                    </td>
                    <td className="px-4 py-3 text-ink-2">{role.users_count ?? 0}</td>
                    <td className="px-4 py-3 text-end">
                      {/* The owner role answers for everything by definition, so
                          there is nothing to edit and nothing to remove. */}
                      {!role.is_owner && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(role);
                            setModalOpen(true);
                          }}
                          className="text-sm font-semibold text-accent-text hover:underline"
                        >
                          {t("common.edit")}
                        </button>
                      )}
                      {!role.is_system && (
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError("");
                            setDeleting(role);
                          }}
                          className="ms-3 text-sm font-semibold text-danger-text hover:underline"
                        >
                          {t("common.delete")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RoleModal
        role={editing}
        catalogue={catalogue}
        implies={implies}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setModalOpen(false);
          setToast({
            type: "success",
            message: t(wasEditing ? "roles.updated" : "roles.created", { name: saved.name }),
          });
          load();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("roles.deleteTitle")}
        message={t("roles.deleteMessage", { name: deleting?.name ?? "" })}
        error={deleteError}
        busy={deleteBusy}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />

      {toast && <Toast type={toast.type} message={toast.message} onDone={() => setToast(null)} />}
    </div>
  );
}
